/**
 * LP ⇄ runtime connection driver.
 *
 * Single source of live truth for the public landing page. It polls
 * the real runtime `/api/status` (same-origin via CloudFront, or the
 * Vite proxy in dev) and:
 *
 *   1. Drives the topbar (now-playing mode, uptime) and the demo bezel
 *      (state pill, fps) from live device state — no more frozen
 *      "CONNECTED" placeholder.
 *   2. Manages the live-panel iframe lifecycle: it's only injected once
 *      the box answers, so a sleeping rig shows a clean standby card
 *      instead of a broken frame. If the box goes away, the frame is
 *      torn down and the standby card returns.
 *   3. Re-publishes each status payload to `window.__lpStatusListeners`
 *      — the contract `drive-gate.ts` subscribes to so it doesn't run
 *      its own poll loop.
 *
 * This replaces the dead `live-wire.ts` no-op as the LP's wire. The LP
 * does not simulate anything; what you see is the device.
 */

type RigState = 'connecting' | 'warming' | 'live' | 'down';

interface LockSnapshot {
  holder_sid: string | null;
  holder_label: string | null;
  seconds_remaining: number | null;
  queue_length: number;
  queue: string[];
  you_are_holder?: boolean;
  your_position?: number | null;
}

interface RuntimeStatus {
  mode?: string;
  fps?: number;
  uptime_seconds?: number;
  lock?: LockSnapshot;
  session?: { sid: string; is_admin: boolean };
  errors?: string[];
}

const POLL_OK_MS = 4000; // when the box is up, status changes are slow
const POLL_DOWN_MS = 8000; // back off when the box is asleep

const MODE_LABEL: Record<string, string> = {
  ambient: 'AMBIENT',
  live: 'LIVE',
  dropdead: 'SAFE STILL',
};
const MODE_COLOR: Record<string, string> = {
  ambient: 'var(--teal)',
  live: 'var(--orange)',
  dropdead: '#888',
};

let _state: RigState = 'connecting';
let _iframeMounted = false;
let _consecutiveFailures = 0;
let _pollTimer: number | null = null;

const $ = (id: string): HTMLElement | null => document.getElementById(id);

function ensureListeners(): Array<(s: RuntimeStatus) => void> {
  const w = window as unknown as {
    __lpStatusListeners?: Array<(s: RuntimeStatus) => void>;
  };
  if (!Array.isArray(w.__lpStatusListeners)) {
    w.__lpStatusListeners = [];
  }
  return w.__lpStatusListeners;
}

function publish(status: RuntimeStatus): void {
  for (const fn of ensureListeners()) {
    try {
      fn(status);
    } catch {
      // a misbehaving subscriber must not break the poll loop
    }
  }
}

function formatUptime(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number): string => n.toString().padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

function mountIframe(): void {
  if (_iframeMounted) return;
  const stage = $('rigStage');
  if (!stage) return;
  const frame = document.createElement('iframe');
  frame.id = 'rigFrame';
  frame.className = 'rig-iframe';
  // The runtime serves its real control panel at /panel — CloudFront
  // routes that path to the EC2 origin, whereas `/` resolves to the S3
  // LP. ?kiosk=1 turns on the touch chrome. Same-origin via CloudFront,
  // so the visitor's session cookie + visitor lock flow through.
  frame.src = '/panel?kiosk=1';
  frame.title = 'GlitchRig live control panel';
  frame.setAttribute('allow', 'microphone; autoplay; clipboard-read; clipboard-write');
  frame.loading = 'lazy';
  // Same-origin (both glitchrig.ai via CloudFront) means we can reach
  // into the panel doc and intercept control interactions. On load,
  // attach a capture-phase guard: scrolling / looking is free, but the
  // first attempt to CHANGE a control (click a chip/button, drag a
  // slider, press a key) by a NON-holder is swallowed and pops the
  // drive gate instead. The holder (you, during your slot) passes
  // through untouched.
  frame.addEventListener('load', () => {
    try {
      attachPanelInteractionGuard(frame);
    } catch {
      // Cross-origin or timing issue — fall back to the visible
      // overlay (still hinted) so there's always a way in.
    }
  });
  stage.appendChild(frame);
  _iframeMounted = true;
  // Reveal the click-to-drive overlay now that the live panel exists.
  // The drive-gate (which knows the lock state) decides whether it
  // shows "tap to drive" / "watching" / hides entirely for the holder.
  const overlay = $('rigDriveOverlay');
  if (overlay) overlay.hidden = false;
}

/** Catch control-interaction attempts inside the same-origin panel and
 *  pop the drive gate for non-holders, WITHOUT blocking scroll/browse.
 *  Lets visitors freely scroll and inspect the panel; only swallows the
 *  gesture that would actually change something (click/keydown on an
 *  interactive element). */
function attachPanelInteractionGuard(frame: HTMLIFrameElement): void {
  const doc = frame.contentDocument;
  if (!doc) return; // not same-origin / not ready

  const isHolder = (): boolean =>
    !!(window as unknown as { __lpIsHolder?: boolean }).__lpIsHolder;
  const openGate = (): void =>
    (window as unknown as { __lpOpenDriveGate?: () => void }).__lpOpenDriveGate?.();

  // Interactive control selectors inside the panel. A click/keydown on
  // any of these by a non-holder should be intercepted. Plain page
  // body / labels / scrolling are NOT in this list, so browsing is free.
  const CONTROL_SEL =
    'button, input, select, textarea, [role="slider"], [role="button"], ' +
    '.chip, .knob, .vec-slot, .vec-auto-pill, .simple-mode-btn, .simple-src-btn, ' +
    '[data-mode], [data-vecmode], [data-look], [data-patch], .flick, .listen-btn';

  const intercept = (e: Event): void => {
    if (isHolder()) return;            // your slot — let it through
    const target = e.target as Element | null;
    if (!target || !target.closest(CONTROL_SEL)) return; // scrolling/looking — allow
    e.preventDefault();
    e.stopPropagation();
    openGate();
  };

  // Capture phase so we see the event before the panel's own handlers.
  // pointerdown covers taps + drag-starts (sliders); click covers
  // keyboard/synthetic activation; keydown covers Enter/Space on
  // focused controls. We do NOT listen for wheel/scroll/touchmove, so
  // scrolling the panel is never blocked.
  doc.addEventListener('pointerdown', intercept, true);
  doc.addEventListener('click', intercept, true);
  doc.addEventListener('keydown', (e) => {
    const ke = e as KeyboardEvent;
    if (ke.key === 'Enter' || ke.key === ' ' || ke.key === 'ArrowUp'
        || ke.key === 'ArrowDown' || ke.key === 'ArrowLeft' || ke.key === 'ArrowRight') {
      intercept(e);
    }
  }, true);
}

function unmountIframe(): void {
  if (!_iframeMounted) return;
  $('rigFrame')?.remove();
  _iframeMounted = false;
  // No panel → no overlay (the standby card covers the stage instead).
  const overlay = $('rigDriveOverlay');
  if (overlay) overlay.hidden = true;
  const badge = $('rigDriveBadge');
  if (badge) badge.hidden = true;
}

function setStandby(title: string, sub: string, dotClass: string, showWake: boolean): void {
  const standby = $('rigStandby');
  if (standby) standby.hidden = false;
  const dot = $('rigStandbyDot');
  if (dot) dot.className = `rig-standby-dot ${dotClass}`;
  const t = $('rigStandbyTitle');
  if (t) t.textContent = title;
  const s = $('rigStandbySub');
  if (s) s.textContent = sub;
  const wake = $('rigWake');
  if (wake) wake.hidden = !showWake;
}

function hideStandby(): void {
  const standby = $('rigStandby');
  if (standby) standby.hidden = true;
}

function setBezel(pill: string, fps: number | undefined): void {
  const pillEl = $('rigStatePill');
  if (pillEl) pillEl.textContent = pill;
  const fpsEl = $('rigFps');
  if (fpsEl) fpsEl.textContent = fps && fps > 0 ? `${fps.toFixed(0)} fps` : '';
}

function applyLive(status: RuntimeStatus): void {
  _state = 'live';
  hideStandby();
  mountIframe();

  const mode = (status.mode ?? 'ambient').toLowerCase();
  setBezel(MODE_LABEL[mode] ?? mode.toUpperCase(), status.fps);

  // topbar
  const tbMode = $('topbar-mode');
  if (tbMode) {
    tbMode.textContent = MODE_LABEL[mode] ?? mode.toUpperCase();
    tbMode.style.color = MODE_COLOR[mode] ?? 'var(--text)';
  }
  const tbUptime = $('topbar-uptime');
  if (tbUptime && typeof status.uptime_seconds === 'number') {
    tbUptime.textContent = formatUptime(status.uptime_seconds);
  }
}

function applyDown(): void {
  // The box answers /api/status only when the runtime is up. A failed
  // fetch means either the EC2 is stopped or the runtime is mid-restart.
  // First couple of misses → "warming"; sustained → "asleep".
  unmountIframe();
  if (_consecutiveFailures <= 2) {
    _state = 'warming';
    setBezel('WARMING…', undefined);
    setStandby(
      'warming up',
      'The rig is booting — compiling shaders. This takes a few seconds.',
      'warming',
      false,
    );
  } else {
    _state = 'down';
    setBezel('ASLEEP', undefined);
    setStandby(
      'the rig is asleep',
      'The live box powers down between sessions to save energy. ' +
        'Reach out and we\u2019ll wake it for a walkthrough.',
      'down',
      true,
    );
  }
  const tbMode = $('topbar-mode');
  if (tbMode) {
    tbMode.textContent = _state === 'warming' ? 'WARMING' : 'ASLEEP';
    tbMode.style.color = 'var(--muted)';
  }
}

async function poll(): Promise<void> {
  let ok = false;
  try {
    const res = await fetch('/api/status', { credentials: 'include' });
    if (res.ok) {
      const status = (await res.json()) as RuntimeStatus;
      _consecutiveFailures = 0;
      applyLive(status);
      publish(status);
      ok = true;
    }
  } catch {
    // network error / box down — fall through to the down path
  }
  if (!ok) {
    _consecutiveFailures += 1;
    applyDown();
  }
  const delay = _state === 'live' ? POLL_OK_MS : POLL_DOWN_MS;
  _pollTimer = window.setTimeout(() => {
    void poll();
  }, delay);
}

export function initConnection(): void {
  if (!$('rigStage')) return; // demo section not present
  ensureListeners();

  // "wake the rig" CTA points at the contact path — we don't expose a
  // public start-the-box button (that's an admin action), but the
  // visitor gets a clear way to ask.
  $('rigWake')?.addEventListener('click', () => {
    window.location.href =
      'mailto:hello@gdna.ai?subject=GlitchRig%20live%20walkthrough';
  });

  void poll();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initConnection);
} else {
  initConnection();
}
